import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";
import { parseToDateValue } from "@/features/FormCard/utils";

import { useEditForm } from "@/features/Edit/hooks";

import {
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { tsfStatusOPCVM } from "@/data/tsf_status";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { dateSchema } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { useTranslation } from "react-i18next";

const table_name = "a7_opcvm_time_serie_files";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  status: z.string().optional().nullable(),
  strategy_id: z
    .preprocess((val) => {
      if (val === "nan" || val === "" || val === null || val === undefined) {
        return null;
      }
      return parseInt(val as string, 10);
    }, z.number().int().nullable())
    .nullable(),
  key: z.string().optional().nullable(),
  error_message: z.string().optional().nullable(),
});

export function A7OPCVMTimeSerieFilesEditCard({ id }: { id: number }) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    client: "a7",
    disable_translations_request: true,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("a7_opcvm_time_serie_files.title", {
                  ns: "tables",
                  defaultValue: "Fichier",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-2">
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <DatePickerField
                    name="date"
                    label={t("a7_opcvm_time_serie_files.columns.date", {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name="strategy_id"
                    label={t(
                      "a7_opcvm_time_serie_files.columns.a7_opcvm_strategy_name",
                      {
                        ns: "tables",
                        defaultValue: "Fonds",
                      },
                    )}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <SelectField
                    name="status"
                    label={t("a7_opcvm_time_serie_files.columns.status", {
                      ns: "tables",
                      defaultValue: "Statut",
                    })}
                    isRequired={true}
                    options={tsfStatusOPCVM}
                    control={form.control}
                  />
                  <InputField
                    name="error_message"
                    label={t(
                      "a7_opcvm_time_serie_files.columns.error_message",
                      {
                        ns: "tables",
                        defaultValue: "Message d'erreur",
                      },
                    )}
                    control={form.control}
                  />
                </div>
                <InputField
                  name="key"
                  label={t("a7_opcvm_time_serie_files.columns.key", {
                    ns: "tables",
                    defaultValue: "Chemin",
                  })}
                  isRequired={true}
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  elementId={id}
                  client="a7"
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("common.confirm_update", {
            defaultValue: "Confirm changes",
          })}
          children={
            changedFields
              ? Diff(changedFields, [], undefined, table_name)
              : t("common.no_changes", { defaultValue: t("no_changes") })
          }
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={closeDeleteModal}
        />
      </div>
    </div>
  );
}
